import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// PERF: only EN is bundled into main.js. Non-EN resources are loaded on
// demand via native dynamic import when the user switches language.
import translationEN from './locales/en/translation.json';

const supported = ['en', 'es', 'pl', 'sv'];
const loaders = {
  es: () => import(/* webpackChunkName: "i18n-es" */ './locales/es/translation.json'),
  pl: () => import(/* webpackChunkName: "i18n-pl" */ './locales/pl/translation.json'),
  sv: () => import(/* webpackChunkName: "i18n-sv" */ './locales/sv/translation.json'),
};
const loaded = { en: true };
const inflight = {};

// Detect the initial language synchronously. URL wins over localStorage wins
// over navigator. This lets us know whether to block initial React render
// until the correct translation chunk has loaded (avoids flash of English
// on non-EN pages).
function detectInitialLang() {
  try {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const m = path.match(/^\/(en|es|pl|sv)(?:\/|$)/);
    if (m) return m[1];
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('i18nextLng');
      if (stored) {
        const code = stored.split('-')[0];
        if (supported.includes(code)) return code;
      }
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
      const code = navigator.language.split('-')[0];
      if (supported.includes(code)) return code;
    }
  } catch (_) {}
  return 'en';
}

const initialLang = detectInitialLang();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: initialLang,
    resources: {
      en: { translation: translationEN }
    },
    fallbackLng: 'en',
    load: 'languageOnly',
    supportedLngs: supported,
    debug: false,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    },
    react: { useSuspense: false }
  });

async function loadBundle(lang) {
  if (loaded[lang] || !loaders[lang]) return;
  if (inflight[lang]) return inflight[lang];
  inflight[lang] = (async () => {
    try {
      const mod = await loaders[lang]();
      const payload = mod.default || mod;
      i18n.addResourceBundle(lang, 'translation', payload, true, true);
      loaded[lang] = true;
    } catch (e) {
      console.error('Failed to load translation chunk', lang, e);
    } finally {
      delete inflight[lang];
    }
  })();
  return inflight[lang];
}

/**
 * Ensure a language bundle is fully loaded and active.
 * Awaits the dynamic import (if needed) AND calls changeLanguage so that
 * the very next React render uses the new strings.
 */
export async function ensureLanguage(lang) {
  const code = (lang || 'en').split('-')[0];
  if (!supported.includes(code)) return;
  await loadBundle(code);
  if (i18n.language !== code) {
    await i18n.changeLanguage(code);
  }
}

// For non-EN initial loads, block React render until the translation is
// loaded. Only costs a small fetch for non-EN users (~9 KB gzipped chunk).
export const initialResourcesReady = initialLang !== 'en'
  ? ensureLanguage(initialLang)
  : Promise.resolve();

// Subsequent language switches load chunks on demand (safety net for any
// component that calls changeLanguage directly without awaiting ensureLanguage).
i18n.on('languageChanged', (lng) => {
  const lang = (lng || 'en').split('-')[0];
  if (!loaded[lang]) loadBundle(lang);
});

export default i18n;
